import { useEffect, type ReactNode } from 'react'
import { clsx } from 'clsx'

// ---------------------------------------------------------------------------
// AppShell — presentational grid scaffold for the full app chrome.
//
// Composes a topbar, sidebar, and main content area using the .app CSS grid
// defined in shared/styles/base.css. Three nav layout variants map to the
// data-driven CSS modifier classes.
//
// Props:
//   topbar     — topbar slot (Topbar component or any ReactNode)
//   sidebar    — sidebar slot (Sidebar component or any ReactNode)
//   children   — page body content; rendered inside <main>
//   navLayout  — grid variant: "side" (default), "narrow", or "top"
//   skipToContentLabel — visible-on-focus skip-link text. English default so
//                the component stays usable without an i18n context (tests,
//                register bundle); surface shells pass a localised string.
//
// a11y: a skip link is the first focusable element and jumps focus past the
// repeated topbar + sidebar to the <main> region (WCAG 2.4.1 Bypass Blocks).
// The link is visually hidden until focused; <main> is the focus target.
//
// This component is presentational (props in → UI out).
// No data fetching, no privileged logic, no secrets.
// ---------------------------------------------------------------------------

export type NavLayout = 'side' | 'narrow' | 'top'

/** Anchor id shared by the skip link and the <main> region. */
const MAIN_CONTENT_ID = 'main-content'

export type AppShellProps = {
  topbar: ReactNode
  sidebar: ReactNode
  children: ReactNode
  navLayout?: NavLayout
  /** Skip-link label. Defaults to English; pass a localised value from shells. */
  skipToContentLabel?: string
  /**
   * Active app locale. Mirrored onto <html lang> so assistive technology reads
   * the page in the language the user actually chose — the server renders the
   * document once, so switching language client-side leaves a stale lang
   * attribute unless something syncs it.
   */
  lang?: string
  /**
   * Opt-in mobile responsiveness. When true, adds the `.app--responsive`
   * modifier so the shell's `@media` rules (sidebar off-canvas at phone width)
   * apply. Surfaces opting in must expose the same navigation in a drawer.
   */
  responsive?: boolean
}

/**
 * Full-viewport app shell grid.
 *
 * - `side`    → standard sidebar on the left (default, no modifier)
 * - `narrow`  → icon-only narrow sidebar (.app--narrow-nav, --sidebar-w: 60px)
 * - `top`     → horizontal top-nav, no sidebar column (.app--top-nav)
 *
 * Usage:
 *   <AppShell topbar={<Topbar ... />} sidebar={<Sidebar ... />} navLayout="side">
 *     <PageContent />
 *   </AppShell>
 */
export function AppShell({
  topbar,
  sidebar,
  children,
  navLayout = 'side',
  skipToContentLabel = 'Skip to main content',
  lang,
  responsive = false,
}: AppShellProps) {
  useEffect(() => {
    if (lang) document.documentElement.lang = lang
  }, [lang])

  const className = clsx(
    'app',
    navLayout === 'narrow' && 'app--narrow-nav',
    navLayout === 'top' && 'app--top-nav',
    responsive && 'app--responsive',
  )

  return (
    <div className={className}>
      {/* Skip link — first focusable element; visually hidden until focused,
          then jumps focus to the main region past the topbar + sidebar. */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:border focus:border-border focus:bg-bg-elev focus:px-4 focus:py-2 focus:text-text focus:no-underline focus:shadow-md"
      >
        {skipToContentLabel}
      </a>
      {topbar}
      {sidebar}
      <main id={MAIN_CONTENT_ID} className="main" tabIndex={-1}>
        {children}
      </main>
    </div>
  )
}
